export default () => ({

	/**
	 * Initialises the parallax element and sets up event listeners.
	 *
	 * Finds the parallax element and caches its speed value from the data attribute.
	 * Sets up event listeners for window scroll and resize events.
	 * Calls the updateParallax method to position the parallax element.
	 *
	 * @return {void}
	 */
	init() {
		// frame = the static wrapper; target = the image we will move
		this.frameEl = this.$el.matches('img') ? this.$el.parentElement : this.$el;
		this.parallaxElement = this.$el.matches('img') ? this.$el : (this.$el.querySelector('img') || this.$el);

		this.parallaxSpeed = parseFloat(this.frameEl.dataset.speed || 0.4);
		this.isAnimationQueued = false;

		this.updateParallax = this.updateParallax.bind(this);
		this.handleScroll = this.handleScroll.bind(this);

		this.setupEventListeners();
		this.updateParallax();
	},

	/**
	 * Clamp a value between a minimum and maximum value.
	 *
	 * @param {number} value The value to be clamped.
	 * @param {number} [min=0] The minimum value that the value should be clamped to.
	 * @param {number} [max=1] The maximum value that the value should be clamped to.
	 * @return {number} The clamped value.
	 */
	clampValue(value, min = 0, max = 1) {
		return Math.min(max, Math.max(min, value));
	},


	/**
	 * Updates the parallax element's translateY CSS property based on the current
	 * scroll position.
	 *
	 * This function is called on page load and on scroll, and uses the following
	 * formula to calculate the translateY value:
	 *
	 *   translateY = ((scrollProgress - 0.5) * 2 * parallaxSpeed * 100)
	 *
	 * Where:
	 *
	 *   scrollProgress = (viewportHeight - elementRect.top) / (viewportHeight + elementRect.height)
	 *
	 * The parallaxSpeed value is a data attribute on the parallax element and is
	 * 100px for every 100px scrolled.
	 *
	 * The translateY value is then set as a CSS style on the parallax element.
	 *
	 * @return {void}
	 */

	updateParallax() {
		// measure the static frame, not the moving image
		const frameRect = this.frameEl.getBoundingClientRect();
		const viewportH = window.innerHeight || 1;

		// progress in [0,1] based on where the frame sits in the viewport
		const progress = this.clampValue((viewportH - frameRect.top) / (viewportH + frameRect.height));

		// move by a fraction of the frame's height (pixels = predictable)
		let deltaPx = (progress - 0.5) * 2 * this.parallaxSpeed * frameRect.height;

		if (this.parallaxElement.height > 600) {
			if (deltaPx < 0) deltaPx = 0;
		}

		// apply to the image only
		this.parallaxElement.style.transform = `translateY(${deltaPx.toFixed(2)}px)`;

		this.isAnimationQueued = false;
	},

	/**
	 * Handles the window scroll event.
	 *
	 * Uses requestAnimationFrame to defer the actual parallax update to the next
	 * animation frame. This prevents the browser from freezing when scrolling.
	 *
	 * @return {void}
	 */
	handleScroll() {
		if (!this.isAnimationQueued) {
			requestAnimationFrame(this.updateParallax);
			this.isAnimationQueued = true;
		}
	},

	/**
	 * Set up event listeners to handle window scroll and resize events.
	 *
	 * The "scroll" event is listened to with the "passive" option set to true to
	 * prevent the browser from freezing when scrolling.
	 *
	 * The "resize" event is listened to in order to update the parallax
	 * transform when the window size changes.
	 */
	setupEventListeners() {
		window.addEventListener('scroll', this.handleScroll, { passive: true });
		window.addEventListener('resize', this.updateParallax);
	},

	/**
	 * Destroy the parallax effect by removing the event listeners for scroll and
	 * resize events.
	 */
	destroy() {
		window.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('resize', this.updateParallax);
	}
});
